import { toContext } from 'ol/render';

import Control from 'ol/control/Control';

import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';

import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';

export const BORDER_GREY = [105, 105, 105];
export const NEUTRAL_STYLE = new Style({
    fill: new Fill({
        color: [211, 211, 211]
    }),
    stroke: new Stroke({
        color: BORDER_GREY,
        width: 1
    })
});

export interface LegendRow {
    title: string;
    typeGeom: string;
    style: Style | string;
}

export interface LegendConfig {
    colors: number[][];
    thresholds: number[];
    hasPie: boolean;
    pieLimits: number[];
    pieSizes: number[];
    pieRows: LegendRow[];

}

export function numberWithCommas(x: number) {
    if (x === null || x === undefined)
        return '';
    const parts = x.toString().split(".");
    let decimals = 2;
    if (parts.length > 1) {
        let zeros = 0;
        var idx = 0
        for (; idx < parts[1].length; idx++) {
            if (parts[1][idx] === '0')
                zeros++;
            else
                break;
        }
        if (idx < parts[1].length && parts[1][idx] === '1')
            zeros+=1;
        decimals = Math.max(zeros + 1, decimals);
        if (parts[1].length > decimals)
            parts[1] = parts[1].substring(0, decimals);
        //console.log(x, decimals, idx, zeros);
    }
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (parts.length>1?`,${parts[1]}`:'');
}

export class Legend extends Control {


    private static legendIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M4 4L4 14 14 14 14 4 4 4zM5 5L13 5 13 13 5 13 5 5zM16 6L16 7 28 7 28 6 16 6zM16 11L16 12 28 12 28 11 16 11zM4 18L4 28 14 28 14 18 4 18zM16 20L16 21 24 21 24 20 16 20zM16 25L16 26 24 26 24 25 16 25z"/></svg>';

    private _wasToggled: boolean;
    private _imgElement: HTMLElement;
    private _tableElement: HTMLElement;
    private _rows: LegendRow[];
    private _pieRows: LegendRow[];
    private _pointRows: LegendRow[];
    private _hasPie: boolean;
    private _pieDims: [number, number];
    private _pieVals: [string, string];
    private _gradientColors: number[][]|null;
    private _midStyle: LegendRow|null;
    private _gradientLabels: string[];
    private _gradientExtent: number;
    private _gradientValues: number;
    private _noGradient: boolean;
    private _fontSize: number;
    private size: [number, number];
    private DPR = window.devicePixelRatio;


    onClickShow(): void {
        this._wasToggled = true;
        this.show();
    }

    onClickHide(): void {
        this._wasToggled = true;
        this.hide();
    }

    wasToggled(): boolean {
        return this._wasToggled;
    }

    constructor(optOptions:any) {
        const options = optOptions ? optOptions : {};

        const element = document.createElement('div');
        super({
            element: element,
            render: options.render,
            target: options.target
        });
        this._wasToggled = false;
        this._gradientLabels = ['x', 'xx'];
        if (options.target) {
            element.className = options.className || "ol-legend";
        } else {
            element.className = (options.className || "ol-legend")
                + " ol-unselectable ol-control "
                + (options.collapsible === false ? ' ol-uncollapsible' : 'ol-collapsed');
            // Show on click
            let button = document.createElement('button');
            button.setAttribute('type', 'button');
            button.appendChild(document.createRange().createContextualFragment(Legend.legendIcon));
            button.addEventListener('click', this.onClickShow.bind(this));
            element.appendChild(button);
            // Hide on click
            button = document.createElement('button');
            button.setAttribute('type', 'button');
            button.className = 'ol-closebox';
            button.addEventListener('click', this.onClickHide.bind(this));
            element.appendChild(button);
        }
        // The legend
        this._imgElement = document.createElement('div');
        this._imgElement.className = 'ol-legendImg';
        element.appendChild(this._imgElement);
        this._tableElement = document.createElement('ul');
        element.appendChild(this._tableElement);
        if (options.fontSize)
            this._fontSize = options.fontSize * 4. / 3;// * this.DPR;

        else
            this._fontSize = 14 * 4. / 3;// * this.DPR;
        this.size = [this._fontSize * 1.5, this._fontSize]

        this._rows = [];
        this._pieRows = [];
        this._pointRows = [];
        this._hasPie = false;
        this._pieDims = [5, 15];
        this._pieVals = ['0', '0'];
        this._gradientColors = null;
        this._midStyle = null;
        this._gradientExtent = 3;
        this._gradientValues = 2;
        this._noGradient = false;
        this.set('margin', options.margin === 0 ? 0 : options.margin * this.DPR || 10 * this.DPR);
        // this.set('title', options.title || '');
        // Set the style
        if (options.collapsed === false) this.show();
        //debugger;
        this.refresh();
    }

    setFixedHeight(val: number) {
        if (val)
            this.element.style.height = val + "px";
        else
            this.element.style.removeProperty('height');
    }


    setPie(pieEnabled: boolean,
        minRadius: number | null = null, maxRadius: number | null = null,
        minValLabel: string | null = null, maxValLabel: string | null = null): void {
        this._hasPie = pieEnabled;
        if (minRadius !== null)
            this._pieDims[0] = minRadius;
        if (maxRadius !== null)
            this._pieDims[1] = maxRadius;
        if (minValLabel)
            this._pieVals[0] = minValLabel;
        if (maxValLabel)
            this._pieVals[1] = maxValLabel;
    }

    setGradient(colors: number[][], numRange: number[], labels?: string[]): void {
        this._gradientColors = colors;
        this._gradientValues = 2;
        if (numRange[0] === numRange[numRange.length-1]) {
            if (numRange[0] === null) {
                this._gradientValues = 0;
            }
            else {
                this._gradientValues = 1;
            }
            this._midStyle = {
                title: 'plm',
                typeGeom: 'Polygon', style: new Style({
                    fill: new Fill({ color: colors[1] }),
                    stroke: new Stroke({ color: 'gray', width: 1 }),
                    text: new Text()
                })
            };
        }
        else {
            this._gradientValues = numRange.length;
        }
        if (labels)
            this._gradientLabels = labels;
        else
            this._gradientLabels = numRange.map((val) => numberWithCommas(val))
        this._gradientExtent = this._gradientValues;
    }

    addRow(row: LegendRow): void {
        if (row)
            this._rows.push(row);
    }

    addPieRow(row: LegendRow): void {
        if (row)
            this._pieRows.push(row);
    }

    addPointRow(row: LegendRow): void {
        if (row)
            this._pointRows.push(row);
    }

    clear(): void {
        this._rows.splice(0, this._rows.length);
        this._pieRows.splice(0, this._pieRows.length);
        this._pointRows.splice(0, this._pointRows.length);
        this._hasPie = false;
        this._gradientColors = null;
        this.hide();
        this.refresh();
    }

    getRow(index: number): LegendRow {
        return this._rows[index];
    }


    getLength(): number {
        return this._rows.length;
    }

    show(): void {
        if (this._rows.length > 0 || this._gradientColors || this._hasPie)
            this.element.classList.remove('ol-collapsed');
    }

    isVisible(): boolean {
        return !this.element.classList.contains('ol-collapsed');
    }

    hide(): void {
        this.element.classList.add('ol-collapsed');
    }

    toggle(): void {
        if (!this.isVisible())
            this.show();
        else
            this.element.classList.toggle('ol-collapsed');
    }

    setInitialVisibility(shown: boolean): void {
        if (!this._wasToggled) {
            if (shown && !this.isVisible())
                this.show();
            if (!shown && this.isVisible())
                this.hide();
        }
    }

    addEffectiveRow(height: number, minWidth: number, str: number | string|null) {
        const row = document.createElement('li');
        row.style.height = height + 'px';
        row.style.minWidth = minWidth + 'px';

        const col = document.createElement('div');
        col.classList.add('legend-text');
        // col.style.paddingLeft = width + 'px';        
        col.textContent = (''+str) || '';
        row.appendChild(col);
        this._tableElement.appendChild(row);
    }

    addEffectivePieRow(isRTL: boolean, height: number, minWidth: number, str: string|null) {
        const row = document.createElement('li');
        row.style.height = height + 'px';

        const col = document.createElement('div');
        col.classList.add('legend-text');
        if (isRTL)
            col.style.marginRight = minWidth + 'px';
        else
            col.style.marginLeft = minWidth + 'px';
        // col.style.paddingLeft = width + 'px';
        col.textContent = str || '';
        row.appendChild(col);
        this._tableElement.appendChild(row);
    }


    refresh(isRTL = false): void {

        const table = this._tableElement;
        // table.innerHTML = '';
        while (table.firstChild) {
            table.removeChild(table.firstChild);
        }

        let canvas = document.createElement('canvas');

        const ctx = canvas.getContext("2d");
        if (ctx)
            ctx.font = ctx.font.replace(/\d+\s*px/, this._fontSize.toString() + "px");
        const maxLen = Math.max(...this._pieVals.map((el) => el ? ctx?.measureText(el).width||0 : 0));
        this.size = [this._fontSize * 1.5, this._fontSize];
        const width = this.size[0] + 2 * this.get('margin');
        const height = this.size[1] + 2 * this.get('margin');
        const minWidth = this._hasPie ? (this._pieDims[1] * 2 + maxLen + 4 * this.get('margin')) : 0;
        canvas.width = 2 * width + minWidth;
        table.style.fontSize = this._fontSize + 'px';
        table.style.lineHeight = height + 'px';

        let supl = 0;
        if (this._gradientColors)
            supl = this._gradientValues === 2 ? this._gradientExtent : this._gradientValues;

        let pieHeight = this._hasPie ? ((this._pieDims[1] + this.get("margin")) * 2) : 0;
        const imageRows = Math.ceil(pieHeight / height);
        const effectivePieRows = this._pieRows.length + this._pointRows.length;
        const pieRows = Math.max(imageRows, effectivePieRows);
        const pieOffset = Math.floor((pieRows - effectivePieRows) / 2);

        pieHeight = imageRows * height;
        canvas.height = (this._rows.length + supl + pieRows) * height;

        while (this._imgElement.lastChild && this._imgElement.firstChild) {
            this._imgElement.removeChild(this._imgElement.lastChild);
        }
        this._imgElement.appendChild(canvas);
        this._imgElement.style.height = ((this._rows.length + supl + pieRows) * height) + 'px';

        if (this._hasPie) {
            for (let i = 0; i < pieOffset; i++) {
                this.addEffectivePieRow(isRTL, height, minWidth, null);
            }
            for (let i = 0; i < this._pieRows.length; i++) {
                const r = this._pieRows[i];
                this.addEffectivePieRow(isRTL, height, minWidth, r.title);
                canvas = this.getPieImage(r, canvas, i + pieOffset, minWidth);
            }
            for (let i = 0; i < this._pointRows.length; i++) {
                const r = this._pointRows[i];
                this.addEffectivePieRow(isRTL, height, minWidth, r.title);
                canvas = this.getPieImage(r, canvas, i + pieOffset + this._pieRows.length, minWidth, true);
            }
            for (let j = effectivePieRows + pieOffset; j < pieRows; j++) {
                this.addEffectivePieRow(isRTL, height, minWidth, null);
            }
            canvas = this.drawPie(canvas, this._pieDims, this._pieVals, (supl + this._rows.length) * height +
                pieRows * height / 2 - this.get("margin"), isRTL);
        }

        if (this._gradientColors) {
            if (this._gradientValues === 1) {
                if (this._gradientLabels !== null) {
                    this.addEffectiveRow(height, minWidth, this._gradientLabels[0]);
                    canvas = this.getStyleImage(this._midStyle, canvas, pieRows);
                }
            }
            else if (this._gradientValues > 1) {
                for (var idx = this._gradientLabels.length - 1; idx >= 0;idx--)
                    this.addEffectiveRow(height, minWidth, this._gradientLabels[idx]);
                canvas = this.getGradientImage(canvas, pieRows);
            }
        }
        for (let i = 0; i < this._rows.length; i++) {
            const r = this._rows[i];
            this.addEffectiveRow(height, minWidth, r.title);
            canvas = this.getStyleImage(r, canvas, i + supl + pieRows);
        }

    }


    getGradientImage(theCanvas: HTMLCanvasElement, row: number): HTMLCanvasElement {
        const width = (this.size[0] + 2 * this.get('margin')) / this.DPR;
        const rowHeight = (this.size[1] + 2 * this.get('margin')) / this.DPR;
        const height = rowHeight * this._gradientExtent;
        // height = height * this._gradientExtent;
        let canvas = theCanvas;
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
        }

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.save();
            const vectorContext = toContext(ctx);

            const cx = width / 2;
            const cy = height / 2 + (theCanvas ? row * rowHeight : 0);
            const sx = this.size[0] / 2 / this.DPR;
            const sy = height / 2 - this.get('margin') / this.DPR;
            //console.log(cy, sy);
            // Draw image
            const style = ctx.createLinearGradient(0, (cy + sy) * this.DPR, 0, (cy - sy) * this.DPR);
            if (this._gradientColors) {
                const gradNo = this._gradientColors.length-1;
                for (var idx = 0; idx <= gradNo; idx++) {
                    style.addColorStop(1. * idx / gradNo, `rgb(${this._gradientColors[idx][0]} ${this._gradientColors[idx][1]} ${this._gradientColors[idx][2]})`);
                }
            }
            vectorContext.setStyle(new Style({
                fill: new Fill({ color: style }),
                stroke: new Stroke({ color: 'gray', width: 1 }),
            }));
            vectorContext.drawGeometry(new Polygon([[[cx - sx, cy - sy],
            [cx + sx, cy - sy],
            [cx + sx, cy + sy],
            [cx - sx, cy + sy],
            [cx - sx, cy - sy]]]));
            ctx.restore();
        }

        return canvas;
    }

    drawPie(theCanvas: HTMLCanvasElement, pieDims: [number, number], pieVals: [string, string], verticalOffset: number, isRTL: boolean): HTMLCanvasElement {
        const margin = this.get('margin');
        const width = this.size[0] + 2 * this.get('margin');
        const height = (this.size[1] + 2 * this.get('margin')) * this._gradientExtent;
        // height = height * this._gradientExtent;
        let canvas = theCanvas;
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
        }

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.save();

            const centerOffset = canvas.height - verticalOffset - margin;
            const baseOffset = centerOffset + pieDims[1];
            const topOffset = [baseOffset - 2 * pieDims[0], centerOffset - pieDims[1]];

            ctx.beginPath();
            ctx.arc(margin + pieDims[1], centerOffset, pieDims[1], 0, 2 * Math.PI);
            ctx.stroke();
            if (pieVals[0] !== pieVals[1]) {
                ctx.beginPath();
                ctx.arc(margin + pieDims[1], baseOffset - pieDims[0], pieDims[0], 0, 2 * Math.PI);
                ctx.stroke();
            }
            ctx.setLineDash([2, 5]);
            ctx.beginPath();
            ctx.moveTo(margin + pieDims[1], topOffset[1]);
            ctx.lineTo(margin + pieDims[1] * 2 + 2 * margin, topOffset[1]);
            ctx.stroke();
            if (pieVals[0] !== pieVals[1]) {
                ctx.beginPath();
                ctx.moveTo(margin + pieDims[1], topOffset[0]);
                ctx.lineTo(margin + pieDims[1] * 2 + 2 * margin, topOffset[0]);
                ctx.stroke();
            }
            let scaleX = 1;
            if (isRTL) {
                ctx.scale(-1, 1);
                ctx.textAlign = "end";
                scaleX = -1;
            }

            ctx.textBaseline = 'middle';
            ctx.font = ctx.font.replace(/\d+\s*px/, this._fontSize.toString() + "px");

            if (pieVals[0] !== pieVals[1] && topOffset[0] - topOffset[1] < this._fontSize) {
                const diffOffset = (this._fontSize - topOffset[0] + topOffset[1]) / 2.;
                topOffset[0] += diffOffset;
                topOffset[1] -= diffOffset;
            }
            if (pieVals[0] !== pieVals[1]
                && topOffset[0] - topOffset[1] > this._fontSize
                && topOffset[1] < this._fontSize / 2.) {
                if (topOffset[0] - this._fontSize / 2. < this._fontSize)
                    topOffset[0] += this._fontSize / 2. - topOffset[1];
                topOffset[1] = this._fontSize / 2.;
            }
            //console.log('legendar',topOffset, this._fontSize);
            ctx.fillText(pieVals[1], scaleX * (margin + pieDims[1] * 2 + 3 * margin), topOffset[1]);
            if (pieVals[0] !== pieVals[1]) {
                ctx.fillText(pieVals[0], scaleX * (margin + pieDims[1] * 2 + 3 * margin), topOffset[0]);
            }
            ctx.restore();
        }
        return canvas;
    }

    getStyleImage(options:any, theCanvas: HTMLCanvasElement, row: number): HTMLCanvasElement {
        options = options || {};
        const height = this.size[1] + 2 * this.get('margin');
        const width = this.size[0] + 2 * this.get('margin');
        let canvas = theCanvas;
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
        }

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.save();
            const vectorContext = toContext(ctx);

            const typeGeom = options.typeGeom;
            let style = options.style;
            if (!(style instanceof Array)) style = [style];

            const cx = width / 2 / this.DPR;
            const cy = (height / 2 + (theCanvas ? row * height : 0)) / this.DPR;
            const sx = this.size[0] / 2 / this.DPR;
            const sy = this.size[1] / 2 / this.DPR;
            // Draw image
            for (let i = 0; i < style.length; i++) {
                const s = style[i];
                vectorContext.setStyle(s);
                switch (typeGeom) {
                    case Point:
                    case 'Point':
                        vectorContext.drawGeometry(new Point([cx, cy]));
                        break;
                    case Polygon:
                    case 'Polygon':
                        vectorContext.drawGeometry(new Polygon([[[cx - sx, cy - sy],
                        [cx + sx, cy - sy],
                        [cx + sx, cy + sy],
                        [cx - sx, cy + sy],
                        [cx - sx, cy - sy]]]));
                        break;
                }
            }

            ctx.restore();
        }

        return canvas;
    }

    getPieImage(options: LegendRow, theCanvas: HTMLCanvasElement, row: number, offset: number, asCircle = false): HTMLCanvasElement {
        options = options || {};
        const width = this.size[0] + 2 * this.get('margin');
        const height = this.size[1] + 2 * this.get('margin');
        let canvas = theCanvas;
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
        }

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.save();
            let style:Style[];
            if (!(options.style instanceof Array))
                style = [options.style as Style];
            else
                style = options.style;

            const cx = offset + this.get('margin') / this.DPR + width / 2;
            const cy = height / 2 + (theCanvas ? row * height : 0);
            const sx = this.size[0] / 2;
            const sy = this.size[1] / 2;
            const oldAlpha = ctx.globalAlpha;
            for (let i = 0; i < style.length; i++) {
                const s = style[i];
                ctx.beginPath();
                ctx.globalAlpha = asCircle ? 1 : 0.7;
                if (asCircle) {
                    ctx.arc(cx, cy, Math.min(sx, sy), 0, 2 * Math.PI);
                }
                else {
                    ctx.moveTo(cx + sx, cy - sy);
                    ctx.lineTo(cx + sx, cy + sy);
                    ctx.lineTo(cx - sx, cy + sy);
                }
                ctx.closePath();
                ctx.lineWidth = 1;
                ctx.strokeStyle = 'gray';
                ctx.stroke();
                ctx.fillStyle = s.getFill()?.getColor() as string;
                ctx.fill();
            }
            ctx.globalAlpha = oldAlpha;
            ctx.restore();
        }

        return canvas;
    }
}